import { useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import { WalkingSession } from '@spike/new-booking-model';
import { useMarketplace, useTimeZone } from 'hooks';
import { DataVaraible } from 'model/NewMultipleBooking';
import { usesSessions } from 'model/Service';
import { SessionBusinessArea } from 'model/ServiceSession';
import { withDraftOccupancy } from 'utils/sessionDraftOccupancy';
import useBookingSessions, { useBookingSessionsByDay } from './useBookingSessions';

interface SessionPickerInput {
    area: SessionBusinessArea;
    serviceId?: number;
    bookingType?: string;
    inHomeService?: boolean;
    isEdit: boolean;
    from?: Moment;
    staffId?: number;
    serviceSessionId?: number | null;
    selectedDates: Array<Moment>;
    drafts: Array<DataVaraible>;
    appointmentId?: number;
}

const DAY_FORMAT = 'YYYY-MM-DD';

// One selected date while several are selected, with the sessions configured on it.
export interface SessionDay {
    day: string;
    date: Moment;
    sessions: Array<WalkingSession>;
    loading: boolean;
    // The sessions clicked on this date, which is also what it books.
    selectedSessionIds: Array<number>;
}

// One appointment to create for a selected session on a given date.
export interface SessionOccurrence {
    // The selected date it belongs to, as `YYYY-MM-DD`.
    day: string;
    from: Moment;
    staffId: number;
    serviceSessionId: number;
}

interface SessionPick {
    serviceId: number;
    day: string;
    sessionId: number;
}

export interface SessionPicker {
    sessionBased: boolean;
    sessionDate?: Moment;
    sessions: Array<WalkingSession>;
    loading: boolean;
    // The clicked session the form shows as selected, which is also the one to send. Undefined
    // when no card was clicked, when the stored link no longer matches the current staff, date and
    // start, or while the day's sessions are loading.
    selectedSessionId?: number;
    sessionStart: (session: WalkingSession) => Moment;
    // Several dates selected (never on edit): every date lists its own sessions and any number of
    // them can be picked. A date with no pick books the typed staff and time, unlinked.
    multiDate: boolean;
    sessionDays: Array<SessionDay>;
    toggleSession: (session: WalkingSession, day: string) => void;
    // One entry per session shown selected across the dates, in date and time order.
    occurrences: Array<SessionOccurrence>;
}

export const useSessionPicker = ({
    area,
    serviceId,
    bookingType,
    inHomeService,
    isEdit,
    from,
    staffId,
    serviceSessionId,
    selectedDates,
    drafts,
    appointmentId
}: SessionPickerInput): SessionPicker => {
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const tz = marketplace.schedule.timeZone;

    // A configured session covers one calendar day, so the picker only applies once the
    // booking is pinned to a single date.
    const sessionDate = isEdit
        ? from?.isValid()
            ? from
            : undefined
        : selectedDates.length === 1
        ? selectedDates[0]
        : undefined;

    const sessionBased = usesSessions(bookingType, Boolean(inHomeService));
    const multiDate = !isEdit && selectedDates.length > 1;

    const { sessions, loading } = useBookingSessions(serviceId, sessionDate, sessionBased, appointmentId);

    const multiDates = useMemo(
        () => (multiDate ? selectedDates.map(date => date.clone().tz(tz).startOf('day')) : []),
        [multiDate, selectedDates, tz]
    );
    const { sessionsByDay, loadingDays } = useBookingSessionsByDay(
        serviceId,
        multiDates,
        sessionBased && multiDate,
        appointmentId
    );
    const [picks, setPicks] = useState<Array<SessionPick>>([]);

    const sessionsWithDraft = useMemo(
        () => withDraftOccupancy(sessions, drafts, serviceId, sessionDate, tz, area),
        [sessions, drafts, serviceId, sessionDate, tz, area]
    );

    // A booking links to a session only when its card is clicked (CUD-5601), never by a typed time
    // that lands on one. What is shown selected is what is sent: the stored link, and only while
    // it still matches the current staff and start, so a stale link never survives.
    const selectedSessionId = useMemo(() => {
        if (!sessionBased || !sessionDate || loading || !from?.isValid() || !staffId || serviceSessionId == null)
            return undefined;

        const startTime = from.clone().tz(tz).format('HH:mm');
        const linked = sessions.find(
            session => session.id === serviceSessionId && session.staffId === staffId && session.startTime === startTime
        );

        return linked?.id;
    }, [sessionBased, sessionDate, loading, sessions, serviceSessionId, from, staffId, tz]);

    const startOn = (session: WalkingSession, date: Moment | undefined): Moment => {
        const [hour, minute] = session.startTime.split(':').map(Number);

        return (date ?? moment.tz(tz)).clone().tz(tz).hour(hour).minute(minute).seconds(0).milliseconds(0).tz(timeZone);
    };

    const sessionStart = (session: WalkingSession): Moment => startOn(session, sessionDate);

    const sessionDays = useMemo((): Array<SessionDay> => {
        if (!sessionBased || !multiDate) return [];

        return multiDates
            .map(date => date.format(DAY_FORMAT))
            .filter((day, index, days) => days.indexOf(day) === index)
            .sort()
            .map(day => {
                const date = moment.tz(day, DAY_FORMAT, tz);
                const loaded = sessionsByDay[day];
                const daySessions = withDraftOccupancy(loaded ?? [], drafts, serviceId, date, tz, area);
                const picked = picks
                    .filter(pick => pick.serviceId === serviceId && pick.day === day)
                    .map(pick => pick.sessionId)
                    .filter(sessionId => daySessions.some(session => session.id === sessionId));

                return {
                    day,
                    date,
                    sessions: daySessions,
                    loading: loaded === undefined && loadingDays.includes(day),
                    selectedSessionIds: picked
                };
            });
    }, [sessionBased, multiDate, multiDates, sessionsByDay, loadingDays, drafts, serviceId, tz, area, picks]);

    // A click on a date's selected session drops it; any other click adds it.
    const toggleSession = (session: WalkingSession, day: string) => {
        if (!serviceId) return;

        setPicks(prev => {
            const isPick = (pick: SessionPick) =>
                pick.serviceId === serviceId && pick.day === day && pick.sessionId === session.id;

            return prev.some(isPick)
                ? prev.filter(pick => !isPick(pick))
                : [...prev, { serviceId, day, sessionId: session.id }];
        });
    };

    const occurrences = useMemo(
        (): Array<SessionOccurrence> =>
            sessionDays.flatMap(entry =>
                entry.sessions
                    .filter(session => entry.selectedSessionIds.includes(session.id))
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map(session => ({
                        day: entry.day,
                        from: startOn(session, entry.date),
                        staffId: session.staffId,
                        serviceSessionId: session.id
                    }))
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- startOn only reads tz and timeZone
        [sessionDays, tz, timeZone]
    );

    return {
        sessionBased,
        sessionDate,
        sessions: sessionsWithDraft,
        loading,
        selectedSessionId,
        sessionStart,
        multiDate,
        sessionDays,
        toggleSession,
        occurrences
    };
};

export default useSessionPicker;
